import type { Landmark } from '../../../../data/types';
import type { Ring, Vec2 } from '../../../../geo/types';
import { dominantAxis, planFrameOf, type PlanFrame } from '../planFrame';
import type { ChapelPlan, Side, SidePlan } from '../churchModel';
import { apseHipU, apseRing, offsetRight } from './nieuweKerkChevet';
import { radiatingChapels, type RadiatingChapel } from './nieuweKerkChapels';

export type { RadiatingChapel } from './nieuweKerkChapels';

const DEG = Math.PI / 180;

/**
 * The Nieuwe Kerk as it stood in 1538 (docs/plans/landmarks.md, "Nieuwe Kerk"), in the plan frame of
 * its traced footprint: u east along the axis, v north. The present church was rebuilt on the same
 * walls after the fire of 1645, which spared the choir and its chapels (Reliwiki), so the trace holds
 * for 1538; places are measured on it and its vertex numbers are given in the doc.
 */
export const NIEUWE_KERK = {
  /* The axis midway between the aisles' traced walls (vertices 15-24 and 102-103). */
  vc: -1.2,
  /* The west front of 1538 on the aisles' traced west wall (vertices 8-13 and 104-109). */
  westU: -43.2,
  /* Five bays of the nave of c.1435 (Reliwiki; eight columns stand in it, oost-online 2024), on the
     south aisle's traced buttresses (vertices 16-23, 29-30, 33-34) up to the transept. */
  naveBays: [-43.2, -35.95, -29.83, -23.2, -16.85],
  /* The nave, the crossing and the choir as wide as the traced transept arms (vertices 36-46, 94-99). */
  half: 7.45,
  /* The roof stands almost 40 m high (De Nieuwe Kerk, press release of 12 Feb 2025); the painting draws
     it 0.55-0.64 of the walls it stands on (nieuwekerk-scan: choir eave at frame row 561, ridge 533,
     foot near 607). */
  ridgeY: 38,
  eaveY: 24,
  /* The aisles and the ambulatory under lean-to roofs, the eaves at 0.41 and the tops at 0.63 of the
     clerestory eave as painted (frame rows 588 and 578). */
  aisle: { eaveY: 10, topY: 15, south: -16.2, north: 13.1 },
  /* The outer aisles, finished over the two eastern bays of the five planned (De Gids 1942, p. 36),
     to their traced walls (vertices 24-36 and 100-102). */
  outerAisles: { u0: -24.0, south: -24.9, north: 22.6, pitch: 55 * DEG },
  /* The transept on its traced arms, three bays each: the south arm at full height, the north arm of
     1530-40 with one bay of three raised (De Gids 1942, p. 29; Reliwiki), its front on the traced
     recess (vertices 96-97); the other two low, with the aisles' eaves. */
  transept: { u0: -10.95, u1: 3.95, south: -30.8, north: 27.7, bays: 3, lowPitch: 55 * DEG },
  /* The choir on the floor plan (Reliwiki plattegrond, registered to the trace; docs/plans/landmarks.md
     5.5): three bays of 6.3 m to arcade piers at u 9.7, 16.0 and 22.3 and a short one to the chord, at
     piers 16 and 11 (u 26.2, the wall's corner 0.16 m east of their centres); the ambulatory 6.5 m
     wide, which stands its outer corners within 0.3 m of piers 42 to 37. */
  choir: { piers: [9.7, 16.0, 22.3], chordU: 26.35, ambulatory: 6.5 },
  /* The Eggert chapel south of the choir, where Willem Eggert (d. 1417) lies buried, and north of it
     the Holy Cross choir of 1421-52, renewed after the fire of 1452, and the Lady choir of 1488 (De
     Gids 1942, pp. 28-29), three bays from the transept (vertices 51-62). */
  choirChapels: { u1: 21.9, bays: 3, south: -23.3, pitch: 55 * DEG },
  /* Octagonal turrets at the west corners of the nave (Visscher 1613 after De Gids; woodcut 1544,
     uva 2215-2300 x 1640-1740), their tips 5 m over the ridge as painted (frame row 524). */
  turrets: { radius: 1.6, bodyY: 25.5, tipY: 43 },
  /* The crossing flèche: an octagonal lead lantern with a crown of pinnacles under a needle (woodcut
     1544, uva 2120-2260 x 1690-1800); its tip 1.25 times the ridge as painted (frame row 516.5). */
  fleche: { radius: 1.4, sink: 2.5, lanternFoot: 38.6, lanternTop: 41.4, crownTop: 43.0, tipY: 47.5 },
  /* Built later or undated, and left out. */
  absent: {
    towerFragment: { u0: -49.8, u1: -43.2, v0: -11.6, v1: 8.0, dated: '1565 and 1646-53' },
    jacobusChapel: { facet: 0, dated: 'about 1542' },
    southPorch: { u0: -5.87, u1: -0.76, v0: -33.8, v1: -30.8, dated: 'Renaissance, on Visscher 1613' },
  },
} as const;

export interface NieuweKerkPlan {
  readonly frame: PlanFrame;
  readonly vc: number;
  /** The nave, crossing and high choir under one roof from the west front to the apse chord. */
  readonly vessel: {
    readonly westU: number;
    readonly chordU: number;
    /** Where the ridge ends over the apse, every facet of the apse's roof at the long slopes' pitch. */
    readonly hipU: number;
    readonly v0: number;
    readonly v1: number;
    readonly eaveY: number;
    readonly ridgeY: number;
    readonly pitch: number;
  };
  readonly naveBays: readonly number[];
  readonly choirBays: readonly number[];
  readonly apse: Ring;
  readonly transept: {
    readonly u0: number;
    readonly u1: number;
    readonly south: number;
    readonly north: number;
    /** Where the raised bay of the north arm ends and its low bays begin. */
    readonly fullNorth: number;
    readonly eaveY: number;
    readonly ridgeY: number;
    readonly southBays: readonly number[];
    readonly northBays: readonly number[];
    readonly low: { readonly v0: number; readonly v1: number; readonly eaveY: number; readonly ridgeY: number };
  };
  readonly aisles: readonly SidePlan[];
  readonly outerAisles: readonly ChapelPlan[];
  /** Walked counter-clockwise from the transept's south-east corner round the apse to its north-east corner. */
  readonly ambulatory: { readonly inner: readonly Vec2[]; readonly outer: readonly Vec2[]; readonly eaveY: number; readonly topY: number };
  readonly radiatingChapels: readonly RadiatingChapel[];
  readonly choirChapels: readonly ChapelPlan[];
  readonly turrets: readonly { readonly center: Vec2; readonly radius: number; readonly bodyY: number; readonly tipY: number }[];
  readonly fleche: {
    readonly center: Vec2;
    readonly radius: number;
    readonly footY: number;
    readonly lanternFoot: number;
    readonly lanternTop: number;
    readonly crownTop: number;
    readonly tipY: number;
  };
}

export function nieuweKerkFrame(landmark: Landmark): PlanFrame {
  return planFrameOf(landmark.footprint, dominantAxis(landmark.footprint, landmark.heading));
}

/** `count` equal bays from a to b, as their boundaries. */
function divide(a: number, b: number, count: number): number[] {
  return Array.from({ length: count + 1 }, (_, k) => (k === count ? b : a + ((b - a) * k) / count));
}

/** Chapels with a gable facing outward, one per bay between the given boundaries. */
function chapelRow(side: Side, bounds: readonly number[], inner: number, outer: number, pitch: number): ChapelPlan[] {
  const eaveY = NIEUWE_KERK.aisle.eaveY;
  return bounds.slice(1).map((u1, k) => {
    const u0 = bounds[k]!;
    return { side, u0, u1, inner, outer, eaveY, ridgeY: eaveY + ((u1 - u0) / 2) * Math.tan(pitch) };
  });
}

function transeptOf(v0: number, v1: number): NieuweKerkPlan['transept'] {
  const t = NIEUWE_KERK.transept;
  const southBays = divide(t.south, v0, t.bays);
  const northBays = divide(v1, t.north, t.bays);
  const low = { v0: northBays[1]!, v1: t.north, eaveY: NIEUWE_KERK.aisle.eaveY, ridgeY: NIEUWE_KERK.aisle.eaveY + NIEUWE_KERK.half * Math.tan(t.lowPitch) };
  return { u0: t.u0, u1: t.u1, south: t.south, north: t.north, fullNorth: low.v0, eaveY: NIEUWE_KERK.eaveY, ridgeY: NIEUWE_KERK.ridgeY, southBays, northBays, low };
}

function aislesOf(v0: number, v1: number): SidePlan[] {
  const a = NIEUWE_KERK.aisle;
  const run = { u0: NIEUWE_KERK.westU, u1: NIEUWE_KERK.transept.u0, eaveY: a.eaveY, topY: a.topY, roof: 'leanTo' as const };
  return [
    { ...run, side: 1, inner: v1, outer: a.north },
    { ...run, side: -1, inner: v0, outer: a.south },
  ];
}

function outerAislesOf(): ChapelPlan[] {
  const o = NIEUWE_KERK.outerAisles;
  const bounds = [o.u0, NIEUWE_KERK.naveBays[4], NIEUWE_KERK.transept.u0];
  return [...chapelRow(1, bounds, NIEUWE_KERK.aisle.north, o.north, o.pitch), ...chapelRow(-1, bounds, NIEUWE_KERK.aisle.south, o.south, o.pitch)];
}

function chevetOf(chordU: number, v0: number, v1: number) {
  const { vc, half, choir, aisle } = NIEUWE_KERK;
  const apse = apseRing(chordU, vc, half);
  const inner: Vec2[] = [[NIEUWE_KERK.transept.u1, v0], ...apse, [NIEUWE_KERK.transept.u1, v1]];
  const outer = offsetRight(inner, choir.ambulatory);
  const chapels: RadiatingChapel[] = radiatingChapels(outer, vc, aisle.eaveY);
  return { apse, ambulatory: { inner, outer, eaveY: aisle.eaveY, topY: aisle.topY }, radiatingChapels: chapels };
}

function choirChapelsOf(): ChapelPlan[] {
  const { vc, half, choir, choirChapels: c, transept } = NIEUWE_KERK;
  const bounds = divide(transept.u1, c.u1, c.bays);
  const reach = half + choir.ambulatory;
  return [...chapelRow(-1, bounds, vc - reach, c.south, c.pitch), ...chapelRow(1, bounds, vc + reach, 2 * vc - c.south, c.pitch)];
}

function crowningsOf(v0: number, v1: number) {
  const { turrets: t, fleche: f, transept, vc, westU, ridgeY } = NIEUWE_KERK;
  const turrets = [v0, v1].map((v) => ({ center: [westU, v] as Vec2, radius: t.radius, bodyY: t.bodyY, tipY: t.tipY }));
  const center: Vec2 = [(transept.u0 + transept.u1) / 2, vc];
  const fleche = { center, radius: f.radius, footY: ridgeY - f.sink, lanternFoot: f.lanternFoot, lanternTop: f.lanternTop, crownTop: f.crownTop, tipY: f.tipY };
  return { turrets, fleche };
}

export function planNieuweKerk(frame: PlanFrame): NieuweKerkPlan {
  const { vc, half, eaveY, ridgeY, westU } = NIEUWE_KERK;
  const [v0, v1] = [vc - half, vc + half];
  const chordU = NIEUWE_KERK.choir.chordU;
  const hipU = apseHipU(chordU, half);
  const vessel = { westU, chordU, hipU, v0, v1, eaveY, ridgeY, pitch: Math.atan2(ridgeY - eaveY, half) };
  return {
    frame,
    vc,
    vessel,
    naveBays: [...NIEUWE_KERK.naveBays, NIEUWE_KERK.transept.u0],
    choirBays: [NIEUWE_KERK.transept.u1, ...NIEUWE_KERK.choir.piers, chordU],
    transept: transeptOf(v0, v1),
    aisles: aislesOf(v0, v1),
    outerAisles: outerAislesOf(),
    ...chevetOf(chordU, v0, v1),
    choirChapels: choirChapelsOf(),
    ...crowningsOf(v0, v1),
  };
}
